/**
 * «Інтеграції» (/settings) — РЕДИЗАЙН (VITE_REDESIGN).
 *
 * Прод без прапорця бачить незмінений SettingsPageLegacy у page.tsx: маршрут
 * не гейтований жодним VITE_FEATURE_*, тож у прод-збірці сторінка присутня.
 *
 * ЗМІНЕНО ЛИШЕ ПРЕЗЕНТАЦІЮ. Сторінка здебільшого декоративна, і такою лишається:
 *  · handleSave — той самий setTimeout(800) + тост, нічого не зберігає;
 *  · ключ API — та сама заглушка, кнопка «Копіювати» так само лише показує
 *    тост і нічого не кладе в буфер;
 *  · поля вебхука й паролів лишаються неконтрольованими, як були.
 * Єдина реальна логіка — checkApiStatus() з GET /counterparties — не змінена.
 *
 * Що виправлено у вигляді:
 *  · власна палітра (17 літералів) — на токени;
 *  · вкладки з червоним підкресленням → SegmentedTabs, той самий компонент, що
 *    в SCADA; кнопки з суцільної червоної заливки → --series-3 (§3);
 *  · статус API за догмою: Доступний → --ok, Недоступний → --err (тут червоний
 *    доречний — це реальна недоступність сервісу), Перевірка → нейтраль;
 *  · картки → .tile, блоки сповіщень і 2FA втратили вкладені рамки (§3);
 *  · нативні <select> → TokenSelect (той самий список, що деінде);
 *  · Inter на h1, 4 Remix-іконки, 2 font-mono — прибрано (§0);
 *  · перемикачі сповіщень не мали жодного стилю стану — сірий овал не
 *    змінювався при кліку. Тепер вони показують свій стан; сам стан, як і
 *    раніше, лише локальний і нікуди не відправляється.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { API_BASE_URL, api, getApiErrorMessage } from '@/services/api';
import { useToast } from '@/hooks/useToast';
import SegmentedTabs from '@/components/base/SegmentedTabs';
import TokenSelect from '@/components/base/TokenSelect';
import {
  Bell, Copy, KeyRound, RefreshCw, Save, ShieldCheck, SlidersHorizontal,
} from 'lucide-react';

type TabKey = 'general' | 'notifications' | 'api' | 'security';

const NOTIF = [
  { key: 'email_alerts', label: 'settings_notif_email_alerts', desc: 'settings_notif_email_alerts_desc' },
  { key: 'dispatch_notifications', label: 'settings_notif_dispatch', desc: 'settings_notif_dispatch_desc' },
  { key: 'forecast_updates', label: 'settings_notif_forecast', desc: 'settings_notif_forecast_desc' },
  { key: 'market_digest', label: 'settings_notif_market', desc: 'settings_notif_market_desc' },
  { key: 'contract_reminders', label: 'settings_notif_contracts', desc: 'settings_notif_contracts_desc' },
];

const LANGS = ['Українська', 'English', 'Polski'];
const ZONES = ['Europe/Kyiv (EET, UTC+2)', 'Europe/Warsaw (CET, UTC+1)', 'UTC'];
const DATE_FMT = ['DD.MM.YYYY', 'YYYY-MM-DD', 'MM/DD/YYYY'];
const NUM_FMT = ['1 234,56 (European)', '1,234.56 (US)'];

export default function SettingsRedesign() {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const [activeTab, setActiveTab] = useState<TabKey>('general');
  const [saving, setSaving] = useState(false);
  const [apiStatus, setApiStatus] = useState<'unknown' | 'online' | 'offline'>('unknown');
  const [apiStatusLoading, setApiStatusLoading] = useState(false);

  // Локальний стан контролів: нативний <select> і checkbox тримали його в DOM,
  // тож для TokenSelect і власного перемикача його треба тримати в React.
  // Це паритет із попередньою поведінкою, а не нова функція — нікуди не йде.
  const [lang, setLang] = useState(LANGS[0]);
  const [zone, setZone] = useState(ZONES[0]);
  const [dateFmt, setDateFmt] = useState(DATE_FMT[0]);
  const [numFmt, setNumFmt] = useState(NUM_FMT[0]);
  const [notif, setNotif] = useState<Record<string, boolean>>(
    () => Object.fromEntries(NOTIF.map((n) => [n.key, true])),
  );

  const checkApiStatus = async () => {
    setApiStatusLoading(true);
    try {
      await api.get('/counterparties');
      setApiStatus('online');
      addToast('API is online and responding', 'success');
    } catch (err) {
      setApiStatus('offline');
      const msg = getApiErrorMessage(err, 'API unreachable');
      addToast(msg, 'error');
    } finally {
      setApiStatusLoading(false);
    }
  };

  useEffect(() => {
    checkApiStatus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Декоративне збереження — залишено як було, свідомо.
  const handleSave = () => {
    setSaving(true);
    setTimeout(() => {
      setSaving(false);
      addToast('Settings saved successfully', 'success');
    }, 800);
  };

  const label = (s: string) => (
    <div className="text-[11px] mb-1.5" style={{ color: 'var(--text-muted)' }}>{s}</div>
  );
  const inputStyle: any = {
    width: '100%', height: 36, borderRadius: 10, background: 'var(--surface-2)',
    color: 'var(--text)', border: '1px solid var(--line)',
    padding: '0 10px', fontSize: 12.5, outline: 'none',
  };

  /** Перемикач на токенах — раніше овал не змінювався при кліку взагалі. */
  const toggle = (on: boolean, onChange: () => void, aria: string) => (
    <button type="button" role="switch" aria-checked={on} aria-label={aria} onClick={onChange}
      className="relative shrink-0 cursor-pointer transition-colors"
      style={{
        width: 40, height: 22, borderRadius: 999,
        background: on ? 'color-mix(in srgb, var(--ok) 22%, transparent)' : 'var(--surface-2)',
        border: `1px solid ${on ? 'var(--ok)' : 'var(--line)'}`,
      }}>
      <span className="absolute top-1/2 -translate-y-1/2 rounded-full transition-all"
        style={{ width: 14, height: 14, left: on ? 22 : 3,
                 background: on ? 'var(--ok)' : 'var(--text-dim)' }} />
    </button>
  );

  const apiTone = apiStatus === 'online' ? 'var(--ok)'
    : apiStatus === 'offline' ? 'var(--err)' : 'var(--text-muted)';
  const apiText = apiStatus === 'unknown' ? t('settings_api_checking')
    : apiStatus === 'online' ? t('settings_api_online') : t('settings_api_offline');

  return (
    <DashboardLayout breadcrumb={[t('nav_set_integrations')]}>
      <div className="min-h-screen -m-4 md:-m-6" style={{ background: 'var(--bg)' }}>
        <div className="px-4 md:px-6 pt-5 pb-10" style={{ fontFamily: 'Urbanist, Inter, sans-serif' }}>

          {/* Заголовок у тілі не дублюємо — назву розділу показує топбар. */}
          <div className="mb-3">
            <SegmentedTabs
              items={[
                { key: 'general', label: t('settings_tab_general'), Icon: SlidersHorizontal },
                { key: 'notifications', label: t('settings_tab_notifications'), Icon: Bell },
                { key: 'api', label: t('settings_tab_api'), Icon: KeyRound },
                { key: 'security', label: t('settings_tab_security'), Icon: ShieldCheck },
              ]}
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k as TabKey)}
            />
          </div>

          <div className="tile p-4 sm:p-6">
            {activeTab === 'general' && (
              <div className="max-w-xl flex flex-col gap-4">
                <div>{label(t('settings_language'))}
                  <TokenSelect value={lang} onChange={setLang} width="100%"
                    ariaLabel={t('settings_language')}
                    options={LANGS.map((x) => ({ value: x, label: x }))} /></div>
                <div>{label(t('settings_timezone'))}
                  <TokenSelect value={zone} onChange={setZone} width="100%"
                    ariaLabel={t('settings_timezone')}
                    options={ZONES.map((x) => ({ value: x, label: x }))} /></div>
                <div>{label(t('settings_date_format'))}
                  <TokenSelect value={dateFmt} onChange={setDateFmt} width="100%"
                    ariaLabel={t('settings_date_format')}
                    options={DATE_FMT.map((x) => ({ value: x, label: x }))} /></div>
                <div>{label(t('settings_number_format'))}
                  <TokenSelect value={numFmt} onChange={setNumFmt} width="100%"
                    ariaLabel={t('settings_number_format')}
                    options={NUM_FMT.map((x) => ({ value: x, label: x }))} /></div>
              </div>
            )}

            {activeTab === 'notifications' && (
              <div className="max-w-xl flex flex-col">
                {/* Рядки розділені лінією, а не вкладеними рамками (§3). */}
                {NOTIF.map((item, i) => (
                  <div key={item.key} className="flex items-center justify-between gap-4 py-3"
                    style={{ borderTop: i > 0 ? '1px solid var(--line)' : undefined }}>
                    <div className="min-w-0">
                      <div className="text-[13px] font-medium" style={{ color: 'var(--text)' }}>{t(item.label)}</div>
                      <div className="text-[11.5px] mt-0.5" style={{ color: 'var(--text-muted)' }}>{t(item.desc)}</div>
                    </div>
                    {toggle(!!notif[item.key], () => setNotif((p) => ({ ...p, [item.key]: !p[item.key] })), t(item.label))}
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'api' && (
              <div className="max-w-xl flex flex-col gap-4">
                <div className="pb-4" style={{ borderBottom: '1px solid var(--line)' }}>
                  <div className="flex items-center justify-between gap-3 mb-1.5">
                    <div className="text-[10.5px] font-bold uppercase"
                      style={{ color: 'var(--text-dim)', letterSpacing: '0.06em' }}>{t('settings_api_base')}</div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-[10.5px] font-semibold px-2 py-0.5 whitespace-nowrap"
                        style={{ borderRadius: 999, color: apiTone, border: `1px solid ${apiTone}`,
                                 background: `color-mix(in srgb, ${apiTone} 12%, transparent)` }}>
                        {apiText}
                      </span>
                      <button onClick={checkApiStatus} disabled={apiStatusLoading}
                        title={t('settings_api_test')}
                        className="inline-flex items-center justify-center cursor-pointer"
                        style={{ width: 28, height: 28, borderRadius: 8, background: 'var(--surface-2)',
                                 border: '1px solid var(--line)', color: 'var(--text-muted)',
                                 opacity: apiStatusLoading ? 0.55 : 1 }}>
                        <RefreshCw size={13} strokeWidth={2} className={apiStatusLoading ? 'animate-spin' : undefined} />
                      </button>
                    </div>
                  </div>
                  <div className="text-[12.5px] break-all" style={{ color: 'var(--text)' }}>{API_BASE_URL}</div>
                </div>

                <div>{label(t('settings_api_key'))}
                  <div className="flex items-center gap-2">
                    {/* Заглушка ключа лишена як була — свідомо. */}
                    <input type="password" value="sk_live_xxxxxxxxxxxxxxxxxxxxxxxx" readOnly
                      style={{ ...inputStyle, flex: 1, color: 'var(--text-muted)' }} />
                    <button onClick={() => addToast('API key copied to clipboard', 'info')}
                      className="inline-flex items-center gap-1.5 px-3 text-xs cursor-pointer whitespace-nowrap shrink-0"
                      style={{ height: 36, borderRadius: 10, background: 'var(--surface-2)',
                               border: '1px solid var(--line)', color: 'var(--text)' }}>
                      <Copy size={14} strokeWidth={2} />{t('settings_copy')}
                    </button>
                  </div>
                </div>

                <div>{label(t('settings_webhook'))}
                  <input type="text" placeholder="https://your-domain.com/webhook" style={inputStyle} /></div>
              </div>
            )}

            {activeTab === 'security' && (
              <div className="max-w-xl flex flex-col gap-4">
                <div>{label(t('settings_pass_current'))}<input type="password" style={inputStyle} /></div>
                <div>{label(t('settings_pass_new'))}<input type="password" style={inputStyle} /></div>
                <div>
                  {label(t('settings_2fa'))}
                  <div className="flex items-center justify-between gap-4 pt-3"
                    style={{ borderTop: '1px solid var(--line)' }}>
                    <div className="text-[12.5px]" style={{ color: 'var(--text)' }}>{t('settings_2fa_desc')}</div>
                    <button className="px-3 text-xs cursor-pointer whitespace-nowrap shrink-0"
                      style={{ height: 32, borderRadius: 8, background: 'var(--series-3)',
                               border: '1px solid var(--series-3)', color: '#fff' }}>
                      {t('settings_2fa_enable')}
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center justify-end mt-6 pt-5" style={{ borderTop: '1px solid var(--line)' }}>
              <button onClick={handleSave} disabled={saving}
                className="inline-flex items-center gap-1.5 px-4 text-xs cursor-pointer whitespace-nowrap"
                style={{ height: 36, borderRadius: 10, background: 'var(--series-3)',
                         border: '1px solid var(--series-3)', color: '#fff',
                         opacity: saving ? 0.6 : 1 }}>
                {saving
                  ? <><RefreshCw size={14} strokeWidth={2} className="animate-spin" />{t('settings_saving')}</>
                  : <><Save size={14} strokeWidth={2} />{t('settings_save')}</>}
              </button>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
